// model 5 with save history and new thread 
import  { useState, useEffect, useRef } from "react";
import bglogin from "../../assets/login_bg.png"

import DoubleWaveIcon from "./DoubleWaveIcon";

const API_BASE = "http://localhost:5244";

function formatInline(text) {
  // very small markdown‑style inline formatter: **bold**, *italic*
  const parts = [];
  let remaining = text;
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    const token = m[0];
    if (token.startsWith("**")) {
      parts.push(
        <strong key={parts.length}>
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("*")) {
      parts.push(
        <em key={parts.length}>
          {token.slice(1, -1)}
        </em>
      );
    }
    lastIndex = m.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function RichAnswer({ text }) {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);

  if (!lines.length) return null;

  const title = lines[0];
  const rest = lines.slice(1);

  const sections = [];
  let current = { title: null, blocks: [] };

  const pushCurrent = () => {
    if (current.title || current.blocks.length) sections.push(current);
  };

  for (const raw of rest) {
    if (/^#+\s+/.test(raw)) {
      pushCurrent();
      current = { title: raw.replace(/^#+\s+/, ""), blocks: [] };
    } else {
      current.blocks.push(raw);
    }
  }
  pushCurrent();

  return (
    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
      {/* main header */}
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          marginBottom: 10,
          borderBottom: "1px solid rgba(55,65,81,0.8)",
          paddingBottom: 6
        }}
      >
        {formatInline(title)}
      </div>

      {/* sections */}
      {sections.map((sec, idx) => (
        <div key={idx} style={{ marginBottom: 12 }}>
          {sec.title && (
            <div
              style={{
                fontWeight: 600,
                marginBottom: 4,
                color: "#e5e7eb"
              }}
            >
              {formatInline(sec.title)}
            </div>
          )}

          {sec.blocks.map((b, i) => {
            // bullets or numbered list
            if (/^(\*|-|\d+\.)\s+/.test(b)) {
              const clean = b.replace(/^(\*|-|\d+\.)\s+/, "");
              return (
                <div
                  key={i}
                  style={{ marginLeft: 16, marginBottom: 3 }}
                >
                  • {formatInline(clean)}
                </div>
              );
            }
            // normal paragraph
            return (
              <div
                key={i}
                style={{ marginBottom: 4 }}
              >
                {formatInline(b)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------- small bar chart (pixel heights) ----------
const ReportBarChart = ({ chart }) => {
  if (!chart || !chart.labels || !chart.values) return null;
  const max = Math.max(...chart.values, 1);
  const maxPx = 120;

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          height: maxPx + 40,
        }}
      >
        {chart.values.map((v, i) => {
          const h = (v / max) * maxPx;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontSize: 10,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: h,
                  minHeight: 4,
                  background: "linear-gradient(180deg,#22c55e,#16a34a)",
                  borderRadius: 8,
                  transition: "height 200ms ease-out",
                }}
              />
              <div style={{ marginTop: 4, color: "#9ca3af" }}>
                {chart.labels[i]}
              </div>
              <div style={{ color: "#e5e7eb" }}>{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- small pie chart (SVG) ----------
const ReportPieChart = ({ chart }) => {
  if (!chart || !chart.labels || !chart.values) return null;
  const total = chart.values.reduce((s, v) => s + v, 0) || 1;
  const colors = ["#22c55e", "#0ea5e9", "#a855f7", "#f97316", "#eab308"];

  let startAngle = 0;

  return (
    <div style={{ marginTop: 12, display: "flex", gap: 16, alignItems: "center" }}>
      <svg width={160} height={160} viewBox="0 0 32 32">
        {chart.values.map((v, i) => {
          const fraction = v / total;
          const endAngle = startAngle + fraction * Math.PI * 2;

          const x1 = 16 + 16 * Math.cos(startAngle);
          const y1 = 16 + 16 * Math.sin(startAngle);
          const x2 = 16 + 16 * Math.cos(endAngle);
          const y2 = 16 + 16 * Math.sin(endAngle);
          const largeArc = fraction > 0.5 ? 1 : 0;

          const pathData = `
            M 16 16
            L ${x1} ${y1}
            A 16 16 0 ${largeArc} 1 ${x2} ${y2}
            Z
          `;

          const color = colors[i % colors.length];
          startAngle = endAngle;

          return <path key={i} d={pathData} fill={color} />;
        })}
      </svg>

      <div style={{ fontSize: 11 }}>
        {chart.values.map((v, i) => {
          const pct = ((v / total) * 100).toFixed(1);
          const color = colors[i % colors.length];
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: color,
                  marginRight: 6,
                }}
              />
              <span style={{ color: "#e5e7eb" }}>{chart.labels[i]}</span>
              <span style={{ marginLeft: 6, color: "#9ca3af" }}>
                {v} ({pct}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- main premium report card ----------
const PremiumReportCard = ({ report }) => {
  // const [chartType, setChartType] = React.useState<"bar" | "pie">("bar");
  const [chartType, setChartType] = useState("bar" || "pie");
  return (
    <div
      style={{
        marginTop: 12,
        padding: 16,
        borderRadius: 18,
        border: "1px solid rgba(148,163,184,0.35)",
        background:
          "radial-gradient(circle at top left,#020617,#020617 45%,#020617 60%)",
        boxShadow: "0 18px 50px rgba(0,0,0,0.75)",
      }}
    >
      {/* title + meta */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{report.title}</div>
          {report.summary && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#d1d5db",
              }}
            >
              {report.summary}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-end",
            fontSize: 10,
            color: "#9ca3af",
          }}
        >
          {report.rangeLabel && (
            <span
              style={{
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.8)",
              }}
            >
              {report.rangeLabel}
            </span>
          )}
          {report.scopeLabel && <span>{report.scopeLabel}</span>}
          {report.updatedLabel && <span>{report.updatedLabel}</span>}
        </div>
      </div>

      {/* KPI row */}
      {report.kpis && report.kpis.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 10,
          }}
        >
          {report.kpis.map((k, i) => (
            <div
              key={i}
              style={{
                minWidth: 150,
                padding: "8px 10px",
                borderRadius: 12,
                border: "1px solid rgba(148,163,184,0.45)",
                background:
                  "linear-gradient(135deg,rgba(15,23,42,0.95),rgba(15,23,42,0.75))",
                fontSize: 11,
              }}
            >
              <div style={{ color: "#9ca3af", fontSize: 10 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#e5e7eb" }}>
                {k.value} {k.unit}
              </div>
              {k.trend && (
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 10,
                    color:
                      k.trendType === "down"
                        ? "#f97373"
                        : k.trendType === "up"
                          ? "#4ade80"
                          : "#9ca3af",
                  }}
                >
                  {k.trend}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* chart + toggle */}
      {report.chart && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <button
              onClick={() => setChartType("bar")}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                border:
                  chartType === "bar"
                    ? "1px solid #22c55e"
                    : "1px solid #4b5563",
                background:
                  chartType === "bar" ? "#022c22" : "transparent",
                color: "#e5e7eb",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Bar chart
            </button>
            <button
              onClick={() => setChartType("pie")}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                border:
                  chartType === "pie"
                    ? "1px solid #22c55e"
                    : "1px solid #4b5563",
                background:
                  chartType === "pie" ? "#022c22" : "transparent",
                color: "#e5e7eb",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Pie chart
            </button>
          </div>

          {chartType === "bar" ? (
            <ReportBarChart chart={report.chart} />
          ) : (
            <ReportPieChart chart={report.chart} />
          )}
        </>
      )}

      {/* insights list */}
      {report.insights && report.insights.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: "#d1d5db" }}>
          {report.insights.map((li, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              • {li}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default function Chat() {
  // threads from DB: { id (uuid), title, messages: [] }
  const [threads, setThreads] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [originalTextBeforeEdit, setOriginalTextBeforeEdit] = useState("");
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const [aiMode, setAiMode] = useState("openai"); // ollama,github
  const [assistantMode, setAssistantMode] = useState("sql");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chartType, setChartType] = useState("bar" || "pie");
  const [chartTypes, setChartTypes] = useState({});

  const [dashboardViews, setDashboardViews] = useState({
    showKpis: true,
    showBar: true,
    showLine: true,
    showDistribution: true,
    showTable: true,
  });




  const activeThread =
    threads.find((t) => t.id === activeThreadId) || threads[0];

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [threads, activeThreadId, loading]);

  // ---------- INITIAL LOAD: threads from DB ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/threads`);
        const j = await res.json();
        if (j.ok) {
          const list = (j.threads || []).map((t) => ({
            id: t.id,
            title: t.title,
            messages: []
          }));
          if (!list.length) {
            const id = await createNewThreadInDb("New chat");
            setThreads([{ id, title: "New chat", messages: [] }]);
            setActiveThreadId(id);
          } else {
            setThreads(list);
            setActiveThreadId(list[0].id);
            await loadThreadMessages(list[0].id);
          }
        } else {
          throw new Error(j.err || "Failed to load threads");
        }
      } catch (e) {
        console.error(e);
        // Fallback: local-only thread if DB not reachable
        const id = "local-1";
        setThreads([{ id, title: "New chat", messages: [] }]);
        setActiveThreadId(id);
      }
    })();
  }, []);

  const loadThreadMessages = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/threads/${id}`);
      const j = await res.json();
      if (!j.ok) return;
      const thread = j.thread;
      const msgs = (thread.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        type: m.type,
        text: m.text || "",
        sql: m.sql || "",
        columns: m.columns || [],
        rows: m.rows || [],
        report: m.report || null,   // <--- add this
        error: null
      }));
      setThreads((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, title: thread.title, messages: msgs } : t
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  // ---------- DB helpers ----------
  const createNewThreadInDb = async (title) => {
    const res = await fetch(`${API_BASE}/api/threads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: title || "New chat" })
    });
    const j = await res.json();
    if (!j.ok) throw new Error(j.err || "Failed to create thread");
    return j.id;
  };

  const saveMessageToDb = async (threadId, msg) => {
    try {
      if (!threadId || String(threadId).startsWith("local-")) return;
      const body = {
        role: msg.role,
        type: msg.type,
        text: msg.text || null,
        sql: msg.sql || null,
        columns: msg.columns || null,
        rows: msg.rows || null,
        report: msg.report || null,   // <--- add this
      };
      await fetch(`${API_BASE}/api/threads/${threadId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
    } catch (e) {
      console.error("Failed to save message", e);
    }
  };

  // ---------- state helpers ----------
  const updateActiveThread = (updater) => {
    setThreads((prev) =>
      prev.map((t) =>
        t.id === activeThreadId ? { ...t, ...updater(t) } : t
      )
    );
  };

  const pushMessage = (msg) => {
    const threadId = activeThreadId;
    const localId = Date.now() + Math.random();

    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId
          ? { ...t, messages: [...t.messages, { id: localId, ...msg }] }
          : t
      )
    );

    // persist to DB in background
    saveMessageToDb(threadId, msg);
  };


  const renameActiveThreadIfNeeded = (firstUserText) => {
    if (!activeThreadId) return;
    const newTitle =
      firstUserText.length > 40
        ? firstUserText.slice(0, 40) + "…"
        : firstUserText;

    // 1) update UI
    setThreads((prev) =>
      prev.map((t) =>
        t.id === activeThreadId && t.title === "New chat"
          ? { ...t, title: newTitle }
          : t
      )
    );

    // 2) persist to DB (fire-and-forget)
    (async () => {
      try {
        if (String(activeThreadId).startsWith("local-")) return;
        await fetch(`${API_BASE}/api/threads/${activeThreadId}/title`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: newTitle })
        });
      } catch (e) {
        console.error("Failed to rename thread", e);
      }
    })();
  };


  const newThread = async () => {
    try {
      const id = await createNewThreadInDb("New chat");
      const thread = { id, title: "New chat", messages: [] };
      setThreads((prev) => [thread, ...prev]);
      setActiveThreadId(id);
      setInput("");
      setStatus("Ready");
    } catch (e) {
      console.error(e);
    }
  };

  const onSelectThread = async (id) => {
    setActiveThreadId(id);
    const t = threads.find((x) => x.id === id);
    if (t && (!t.messages || !t.messages.length)) {
      await loadThreadMessages(id);
    }
  };

  // ---------- CSV ----------
  const downloadCsv = (columns, rows, filename = "results.csv") => {
    if (!columns || !rows) return;

    const header = columns.join(",");
    const body = rows
      .map((r) =>
        r
          .split("|")
          .map((cell) => {
            const v = cell === "NULL" ? "" : cell ?? "";
            const escaped = String(v).replace(/"/g, '""');
            return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
          })
          .join(",")
      )
      .join("\n");

    const csv = header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---------- send / SQL flow ----------

  const handleSend = async () => {

    if (!input.trim() || loading || !activeThreadId) return;

    const user = input.trim();
    setInput("");

    // (unchanged) message editing logic
    if (editingMessageId) {
      let cutMessageId = editingMessageId;

      setThreads(prev =>
        prev.map(t => {
          if (t.id !== activeThreadId) return t;

          const idx = t.messages.findIndex(m => m.id === editingMessageId);
          if (idx === -1) return t;

          const updated = t.messages.map((m, i) =>
            i === idx && m.role === "user" ? { ...m, text: user } : m
          );

          cutMessageId = updated[idx].id;

          return {
            ...t,
            messages: updated.slice(0, idx + 1)
          };
        })
      );

      setEditingMessageId(null);
      await deleteMessagesAfter(activeThreadId, cutMessageId);
    }
    else {
      if (!activeThread?.messages?.length) {
        renameActiveThreadIfNeeded(user);
      }
      pushMessage({ role: "user", type: "chat", text: user });
    }


    if (assistantMode === "helpdesk") {
      await handleHelpdeskFlow(user);
      return;
    }
    else if (assistantMode === "reporting") {
      await handleReportingFlow(user);
      return;
    }
    else {
      setLoading(true);
      setStatus("Checking if this is a database question…");

      try {
        // DB classifier
        const cRes = await fetch(`${API_BASE}/api/classify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user })
        });

        const cText = (await cRes.text()).trim().toUpperCase();
        const isDb = cText.startsWith("YES");

        if (isDb) {
          // SQL FLOW (unchanged)
          setStatus("Generating SQL from your question…");

          const genRes = await fetch(`${API_BASE}/api/generate-sql`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user })
          });

          const genJson = await genRes.json();
          console.log("generate-sql:", genJson);

          if (!genJson.success) {
            setStatus(genJson.reason || "Could not generate SQL.");
            pushMessage({
              role: "assistant",
              type: "chat",
              text: genJson.reason || "I could not generate SQL."
            });
            return;
          }

          if (genJson.type === "chat") {
            pushMessage({ role: "assistant", type: "chat", text: genJson.reply });
            return;
          }

          if (genJson.type === "sql") {
            const sql = genJson.sql.trim();
            setStatus("SQL generated. Executing…");
            await runSqlMessage(sql);
            return;
          }

        } else {
          // NORMAL CHAT → now uses AI MODE
          setStatus("Getting normal chat answer…");

          const replyRes = await fetch(`${API_BASE}/api/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              // engine: aiMode,   // 👈 NEW LINE (ollama or github)
              provider: aiMode,   // 👈 NEW LINE (ollama or github)
              prompt: user
            })
          });

          const j = await replyRes.json();

          pushMessage({
            role: "assistant",
            type: "chat",
            text: j.response || "No response."
          });
        }
      }
      catch (err) {
        console.error(err);
        setStatus(`Error: ${err.message}`);
        pushMessage({
          role: "assistant",
          type: "chat",
          text: `Error: ${err.message}`
        });
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleHelpdeskFlow = async (user) => {
    setLoading(true);
    setStatus("Getting support answer...");
    try {
      const res = await fetch(`${API_BASE}/api/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user, threadId: activeThreadId }),
      });
      const j = await res.json();
      if (!j.ok) {
        setStatus(j.err || "Support error");
        pushMessage({
          role: "assistant",
          type: "chat",
          text: j.err || "Sorry, I could not answer this support question.",
        });
        return;
      }
      pushMessage({
        role: "assistant",
        type: "chat",
        text: j.reply || "No reply.",
      });
      setStatus("Ready");
    } catch (err) {
      console.error(err);
      setStatus("Support error");
      pushMessage({
        role: "assistant",
        type: "chat",
        text: `Error: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };  


  const handleReportingFlow = async (user) => {
    setLoading(true);
    setStatus("Generating report and dashboard...");

    try {
      // 1) generate + execute SQL (auto-executed endpoint)
      const genRes = await fetch(`${API_BASE}/api/generate-sql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user }),
      });
      const genJson = await genRes.json();

      if (!genJson.success || genJson.type !== "sql") {
        setStatus(genJson.reason || "Could not generate SQL.");
        pushMessage({
          role: "assistant",
          type: "chat",
          text:
            genJson.reply ||
            genJson.reason ||
            "I could not generate a database query for this report.",
        });
        return;
      }

      // 2) ask reporting endpoint for narrative + KPIs
      setStatus("Summarizing local data into a report...");
      const repRes = await fetch(`${API_BASE}/api/reporting/from-rows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: user,
          columns: genJson.columns,
          rows: genJson.rows,
        }),
      });

      const result = await repRes.json();

      // 3) Use sqlColumns/sqlRows from report (full dataset) for chart
      const columns = result.sqlColumns || genJson.columns || [];
      const rows = result.sqlRows || genJson.rows || [];

      pushMessage({
        role: "assistant",
        type: result.type || "report",
        text: result.reply || "Report generated.",
        report:
          result.type === "report"
            ? {
              title: result.title,
              summary: result.summary,
              kpis: result.kpis,
              labelColumn: result.labelColumn,
              valueColumn: result.valueColumn
              // chart config is built in UI layer from columns + rows
            }
            : null,
        sql: genJson.sql,
        columns,
        rows,
      });

      setStatus(
        rows && rows.length
          ? `Report generated from ${rows.length} rows.`
          : "Report generated (no rows)."
      );
    } catch (err) {
      console.error(err);
      setStatus("Reporting error.");
      pushMessage({
        role: "assistant",
        type: "chat",
        text: `Error: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };


  const runSqlMessage = async (sql, existingId = null) => {
    if (!sql.trim() || !activeThreadId) return;
    setLoading(true);
    setStatus("Executing SQL…");

    try {
      const execRes = await fetch(`${API_BASE}/api/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sql })
      });
      const execJson = await execRes.json();
      console.log("execute:", execJson);

      if (!execJson.ok) {
        const payload = {
          role: "assistant",
          type: "sql",
          sql,
          columns: [],
          rows: [],
          error: execJson.err || execJson.reason || "Unknown SQL error."
        };

        if (existingId) {
          // update only in React state; do NOT save again
          updateActiveThread((t) => ({
            ...t,
            messages: t.messages.map((m) =>
              m.id === existingId ? { ...m, ...payload } : m
            )
          }));
        } else {
          // this already saves to DB via pushMessage -> saveMessageToDb
          pushMessage(payload);
        }

        setStatus("Query execution failed.");
        return;
      }

      const payload = {
        role: "assistant",
        type: "sql",
        sql,
        columns: execJson.columns || [],
        rows: execJson.rows || [],
        error: null
      };

      if (existingId) {
        // update only in React state
        updateActiveThread((t) => ({
          ...t,
          messages: t.messages.map((m) =>
            m.id === existingId ? { ...m, ...payload } : m
          )
        }));
      } else {
        // single insert + single DB write
        pushMessage(payload);
      }

      if (!execJson.rows || execJson.rows.length === 0) {
        setStatus("Query executed – no rows returned.");
      } else {
        setStatus(`Query executed – ${execJson.rows.length} rows returned.`);
      }
    } catch (err) {
      console.error(err);
      const payload = {
        role: "assistant",
        type: "sql",
        sql,
        columns: [],
        rows: [],
        error: err.message
      };

      if (existingId) {
        updateActiveThread((t) => ({
          ...t,
          messages: t.messages.map((m) =>
            m.id === existingId ? { ...m, ...payload } : m
          )
        }));
      } else {
        pushMessage(payload);
      }

      setStatus("Error while executing SQL.");
    } finally {
      setLoading(false);
    }
  };


  const deleteThread = async (id) => {
    try {
      if (!id || String(id).startsWith("local-")) {
        // just remove from UI
        setThreads(prev => prev.filter(t => t.id !== id));
        if (activeThreadId === id) {
          const next = threads.find(t => t.id !== id);
          setActiveThreadId(next ? next.id : null);
        }
        return;
      }
      await fetch(`${API_BASE}/api/threads/${id}`, { method: "DELETE" });
      setThreads(prev => prev.filter(t => t.id !== id));
      if (activeThreadId === id) {
        const next = threads.find(t => t.id !== id);
        setActiveThreadId(next ? next.id : null);
      }
    } catch (e) {
      console.error("Failed to delete thread", e);
    }
  };

  const deleteAllThreads = async () => {
    try {
      await fetch(`${API_BASE}/api/threads`, { method: "DELETE" });
    } catch (e) {
      console.error("Failed to delete all threads", e);
    } finally {
      setThreads([]);
      setActiveThreadId(null);
      setInput("");
      setStatus("All history cleared.");
    }
  };

  const deleteMessagesAfter = async (threadId, messageId) => {
    try {
      if (!threadId || String(threadId).startsWith("local-")) return;
      await fetch(`${API_BASE}/api/threads/${threadId}/messages/after/${messageId}`, {
        method: "DELETE"
      });
    } catch (e) {
      console.error("Failed to delete messages after", e);
    }
  };


  const handleSqlEdit = (id, newSql) => {
    updateActiveThread((t) => ({
      ...t,
      messages: t.messages.map((m) =>
        m.id === id ? { ...m, sql: newSql } : m
      )
    }));
  };

  const handleSqlReRun = async (id, sql) => {
    await runSqlMessage(sql, id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [viewSQLArea, setViewSQLArea] = useState(false);

  const ReportBarChart = ({ chart }) => {
    if (!chart || !chart.labels || !chart.values) return null;

    const max = Math.max(...chart.values, 1);  // avoid divide by 0
    const maxPx = 140;                         // max bar height in px

    return (
      <div style={{ width: "100%", height: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            minWidth: chart.labels.length * 70,  // creates horizontal scroll
          }}
        >
          {chart.values.map((v, i) => {
            const h = (v / max) * maxPx;       // 0..maxPx
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  fontSize: 10,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: h,                 // pixels, not %
                    minHeight: 4,
                    background:
                      "linear-gradient(180deg,#22c55e,#16a34a)",
                    borderRadius: 6,
                    transition: "height 200ms ease-out",
                  }}
                />
                <div style={{ marginTop: 6, color: "#9ca3af" }}>
                  {chart.labels[i]}
                </div>
                <div style={{ color: "#e5e7eb" }}>{v}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  const ReportPieChart = ({ chart }) => {
    if (!chart || !chart.labels || !chart.values) return null;
    const total = chart.values.reduce((s, v) => s + v, 0) || 1;

    // cumulative angles
    let startAngle = 0;

    const colors = ["#22c55e", "#0ea5e9", "#a855f7", "#f97316", "#facc15"];

    return (
      <div style={{ marginTop: 20, display: "flex", gap: 16, alignItems: "center" }}>
        {/* SVG pie */}
        <svg width={180} height={180} viewBox="0 0 32 32">
          {chart.values.map((v, i) => {
            const fraction = v / total;
            const endAngle = startAngle + fraction * Math.PI * 2;

            const x1 = 16 + 16 * Math.cos(startAngle);
            const y1 = 16 + 16 * Math.sin(startAngle);
            const x2 = 16 + 16 * Math.cos(endAngle);
            const y2 = 16 + 16 * Math.sin(endAngle);
            const largeArc = fraction > 0.5 ? 1 : 0;

            const pathData = `
            M 16 16
            L ${x1} ${y1}
            A 16 16 0 ${largeArc} 1 ${x2} ${y2}
            Z
          `;

            const color = colors[i % colors.length];
            startAngle = endAngle;

            return <path key={i} d={pathData} fill={color} />;
          })}
        </svg>

        {/* Legend */}
        <div
          style={{
            fontSize: 11,
            maxHeight: 260,          // limit height
            overflowY: "auto",       // scroll when many items
            paddingRight: 6,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              columnGap: 16,
              rowGap: 4,
            }}
          >
            {chart.values.map((v, i) => {
              const pct = ((v / total) * 100).toFixed(1);
              const color = colors[i % colors.length];
              const label = chart.labels[i];

              // Optional: shorten very long labels
              const shortLabel =
                label && label.length > 28 ? label.slice(0, 28) + "…" : label;

              return (
                <div
                  key={i}
                  style={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  title={label}           // full text on hover
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: color,
                      marginRight: 6,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      color: "#e5e7eb",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 140,
                      flexShrink: 1,
                    }}
                  >
                    {shortLabel}
                  </span>
                  <span
                    style={{
                      marginLeft: 6,
                      color: "#9ca3af",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>



      </div>
    );
  };

  function buildChartFromResult(columns, rows, labelColumn, valueColumn) {
    if (!columns || !rows || !columns.length) return { labels: [], values: [] };

    const lower = columns.map((c) => c.toLowerCase());
    const idxOf = (name) =>
      name
        ? lower.indexOf(name.toLowerCase())
        : -1;

    let labelIdx = idxOf(labelColumn);
    let valueIdx = idxOf(valueColumn);

    // Fallbacks if AI returned something unexpected
    if (labelIdx === -1) {
      labelIdx = columns.findIndex((c) =>
        /name|code|party|customer|product|item|roll|pallet/i.test(c)
      );
      if (labelIdx === -1) labelIdx = 0;
    }

    if (valueIdx === -1) {
      valueIdx = columns.findIndex((c) =>
        /qty|quantity|amount|total|count|value|weight|kg/i.test(c)
      );
      if (valueIdx === -1)
        valueIdx = Math.min(1, Math.max(columns.length - 1, 0));
    }

    const labels = [];
    const values = [];

    rows.forEach((r) => {
      const cells = r.split("|");
      labels.push(cells[labelIdx] ?? "");
      values.push(Number(cells[valueIdx] ?? 0));
    });

    return { labels, values };
  }


  const DashboardSection = ({ report, columns, rows }) => {
    const { labelColumn, valueColumn } = report || {};
    const { labels, values } = buildChartFromResult(
      columns || [],
      rows || [],
      labelColumn,
      valueColumn
    );
    if (!labels.length) return null;

    const chartData = { labels, values };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Top: KPI cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 8,
          }}
        >
          {report.kpis?.map((k, i) => (
            <div
              key={i}
              style={{
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.5)",
                background: "#020617",
                fontSize: 11,
              }}
            >
              <div style={{ fontSize: 10, color: "#9ca3af" }}>{k.label}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {k.value} {k.unit}
              </div>
            </div>
          ))}
        </div>

        {/* Middle: bar + line */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.2fr)",
            gap: 16,
          }}
        >
          {/* comparison bar */}
          <div
            style={{
              maxHeight: 260,
              overflowX: "auto",
              overflowY: "hidden",
              paddingBottom: 4,
            }}
          >
            <ReportBarChart chart={chartData} />
          </div>

          {/* simple trend line (can reuse same data for now) */}
          <div
            style={{
              maxHeight: 260,
              overflowX: "auto",
              overflowY: "hidden",
              paddingBottom: 4,
            }}
          >
            <ReportLineChart chart={chartData} />
          </div>
        </div>

        {/* Bottom: distribution + table */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
            gap: 16,
          }}
        >
          <div
            style={{
              maxHeight: 260,
              overflowY: "auto",
              paddingBottom: 4,
            }}
          >
            <ReportPieChart chart={chartData} />
          </div>

          <div
            style={{
              maxHeight: 260,
              overflowY: "auto",
              borderRadius: 10,
              border: "1px solid rgba(55,65,81,0.9)",
              background: "#020617",
            }}
          >
            {/* reuse your existing table rendering for columns/rows */}
            <ReportTable columns={columns} rows={rows} />
          </div>
        </div>
      </div>
    );
  };

  const ReportLineChart = ({ chart }) => {
    if (!chart || !chart.labels || !chart.values) return null;

    const values = chart.values.map((v) => Number(v) || 0);
    const max = Math.max(...values, 1);

    const width = 260;
    const height = 160;
    const stepX = values.length > 1 ? width / (values.length - 1) : 0;

    const points = values
      .map((v, i) => {
        const x = i * stepX;
        const y = height - (v / max) * (height - 10);
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ background: "#020617", borderRadius: 10 }}
      >
        <polyline
          fill="none"
          stroke="#22c55e"
          strokeWidth="2"
          points={points}
        />
      </svg>
    );
  };
  const ReportTable = ({ columns, rows }) => {
    if (!columns || !rows || !columns.length) return null;

    return (
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 11,
        }}
      >
        <thead>
          <tr
            style={{
              background: "linear-gradient(135deg,#1d4ed8,#0ea5e9)",
              color: "#e5e7eb",
            }}
          >
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  padding: 6,
                  textAlign: "left",
                  borderRight: "1px solid rgba(30,64,175,0.7)",
                  fontWeight: 600,
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, ri) => (
            <tr
              key={ri}
              style={{
                background: "#020617",
                borderTop: "1px solid rgba(31,41,55,0.9)",
              }}
            >
              {r.split("|").map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: 6,
                    borderRight: "1px solid rgba(31,41,55,0.8)",
                    color: "#e5e7eb",
                    wordBreak: "break-word",
                  }}
                >
                  {cell === "NULL" ? (
                    <span style={{ color: "#6b7280" }}>NULL</span>
                  ) : (
                    cell || "-"
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };





  // modern ai format
  return (
    <div

      style={{
        height: "92.5vh",        // fixed
        maxHeight: "100vh",

        overflow: "hidden",     // page itself never scrolls
        display: "flex",
        justifyContent: "center",
        padding: "2px 2px",
        background: "radial-gradient(circle at top, #1f2937, #020617)",
        color: "#e5e7eb",
      }}
    >
      <div
        style={{
          width: "100%",
          // maxWidth: 1280,
          display: "flex",
          // borderRadius: 24,
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(15,23,42,0.96), rgba(15,23,42,0.96))",
          boxShadow: "0 32px 120px rgba(0,0,0,0.9)",
          border: "1px solid rgba(148,163,184,0.2)",
          position: "relative",
        }}
      >
        {/* subtle bg image layer */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bglogin})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
            filter: "blur(4px)",
            pointerEvents: "none",
          }}
        />

        {/* content layer */}
        <div
          style={{
            position: "relative",
            display: "flex",
            width: "100%",
          }}
        >
          {/* Sidebar (threads) */}
          <div
            style={{
              width: sidebarOpen ? 260 : 0,
              opacity: sidebarOpen ? 1 : 0,
              borderRight: sidebarOpen
                ? "1px solid rgba(31,41,55,0.9)"
                : "none",
              padding: sidebarOpen ? 12 : 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background:
                "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(15,23,42,0.98))",
              backdropFilter: "blur(20px)",
              overflow: "hidden",
              transition:
                "width 220ms ease-out, opacity 180ms ease-out, padding 180ms ease-out, border-right 220ms ease-out",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 0.08,
                }}
              >
                Chats
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#6b7280",
                  fontSize: 14,
                  cursor: "pointer",
                }}
                title="Hide sidebar"
              >
                ✕
              </button>
            </div>

            <button
              onClick={newThread}
              style={{
                padding: "8px 10px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.6)",
                background: "linear-gradient(135deg,#22c55e,#16a34a)",
                color: "#f9fafb",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: 4,
                boxShadow: "0 10px 30px rgba(34,197,94,0.35)",
              }}
            >
              + New chat
            </button>

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                fontSize: 13,
                paddingRight: 4,

              }}
            >
              {threads.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                    gap: 6,
                  }}
                >
                  <div
                    onClick={() => onSelectThread(t.id)}
                    style={{
                      flex: 1,
                      padding: "7px 9px",
                      borderRadius: 12,
                      cursor: "pointer",
                      background:
                        t.id === activeThreadId
                          ? "rgba(55,65,81,0.95)"
                          : "transparent",
                      color:
                        t.id === activeThreadId ? "#f9fafb" : "#9ca3af",
                      border:
                        t.id === activeThreadId
                          ? "1px solid rgba(148,163,184,0.8)"
                          : "1px solid transparent",
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={t.title}
                  >
                    {t.title}
                  </div>
                  <button
                    onClick={() => deleteThread(t.id)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#6b7280",
                      cursor: "pointer",
                      fontSize: 11,
                      padding: 2,
                    }}
                    title="Delete chat"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={deleteAllThreads}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(248,113,113,0.7)",
                background: "rgba(127,29,29,0.35)",
                color: "#fecaca",
                fontSize: 12,
                cursor: "pointer",
                marginTop: 4,
              }}
            >
              🗑 Clear all
            </button>
          </div>

          {/* Main chat column */}
          <div
            style={{
              flex: 1,
              minWidth: 0,              // ← important
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "10px 20px",
                borderBottom: "1px solid rgba(31,41,55,0.9)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background:
                  "linear-gradient(90deg,rgba(15,23,42,0.95),rgba(15,23,42,0.98))",
                backdropFilter: "blur(18px)",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <button
                  onClick={() => setSidebarOpen((v) => !v)}
                  title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.6)",
                    background: "rgba(15,23,42,0.9)",
                    color: "#e5e7eb",
                    fontSize: 14,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition:
                      "background 160ms ease-out, border 160ms ease-out, transform 120ms ease-out",
                  }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.transform = "scale(0.95)")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  {sidebarOpen ? "☰" : "☰"}

                </button>

                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    boxShadow: "0 0 12px rgba(251,191,36,0.4)",
                  }}
                >
                  {/* 🤖 */}
                  <DoubleWaveIcon size={32} strokeWidth={1.4} />
                </div>


                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    Papy AI
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      lineHeight: 1.2,
                    }}
                  >
                    {assistantMode === "sql"
                      ? "SQL / Analytics"
                      : assistantMode === "helpdesk"
                        ? "Support / Helpdesk"
                        : "Reporting / Dashboards"}{" "}
                    · {aiMode}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <select
                      value={assistantMode}
                      onChange={(e) => setAssistantMode(e.target.value)}
                      style={{
                        fontSize: 11,
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(148,163,184,0.4)",
                        background: "#020617",
                        color: "#e5e7eb",
                      }}
                    >
                      <option value="sql">SQL / Analytics</option>
                      <option value="helpdesk">Support / Helpdesk</option>
                      <option value="reporting">
                        Reporting / Dashboards
                      </option>
                    </select>
                    <select
                      value={aiMode}
                      onChange={(e) => setAiMode(e.target.value)}
                      style={{
                        fontSize: 11,
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(148,163,184,0.4)",
                        background: "#020617",
                        color: "#e5e7eb",
                      }}
                    >
                      <option value="openai">openai</option>
                      <option value="ollama">Ollama</option>
                      <option value="github">GitHub Models</option>
                      <option value="sambanova">SambaNova</option>
                    </select>
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: loading ? "#facc15" : "#9ca3af",
                  padding: "4px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.5)",
                  background: "rgba(15,23,42,0.8)",
                  // maxWidth: 260,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {status || (loading ? "Working…" : "Ready")}
              </div>
            </div>

            {/* Messages area (center column like ChatGPT / Perplexity) */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px 0",
              }}
            >
              <div
                style={{
                  margin: "0 auto",
                  padding: "0 18px",
                }}
              >
                {(!activeThread || !activeThread.messages?.length) &&
                  !loading && (
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: 40,
                        color: "#9ca3af",
                        fontSize: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          marginBottom: 8,
                        }}
                      >
                        Welcome to Papy AI
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 16 }}>
                        Ask about customers, sales, inventory, or any ERP
                        question.
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          justifyContent: "center",
                          fontSize: 12,
                        }}
                      >
                        <button
                          onClick={() =>
                            setInput("Top 5 customers by sales") ||
                            inputRef.current?.focus()
                          }
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            border:
                              "1px solid rgba(148,163,184,0.5)",
                            background: "rgba(15,23,42,0.9)",
                            color: "#e5e7eb",
                            cursor: "pointer",
                          }}
                        >
                          Top 5 customers by sales
                        </button>
                        <button
                          onClick={() =>
                            setInput("Sales orders last 7 days") ||
                            inputRef.current?.focus()
                          }
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            border:
                              "1px solid rgba(148,163,184,0.5)",
                            background: "rgba(15,23,42,0.9)",
                            color: "#e5e7eb",
                            cursor: "pointer",
                          }}
                        >
                          Sales orders last 7 days
                        </button>
                        <button
                          onClick={() =>
                            setInput("Current stock by item") ||
                            inputRef.current?.focus()
                          }
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            border:
                              "1px solid rgba(148,163,184,0.5)",
                            background: "rgba(15,23,42,0.9)",
                            color: "#e5e7eb",
                            cursor: "pointer",
                          }}
                        >
                          Current stock by item
                        </button>
                      </div>
                    </div>
                  )}

                {activeThread?.messages?.map((m) => (

                  <div
                    key={m.id}
                    style={{
                      display: "flex",
                      justifyContent:
                        m.role === "user" ? "flex-end" : "flex-start",
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "65%",          // or 520
                        borderRadius: 18,
                        padding: 12,
                        whiteSpace: "pre-wrap",   // already used for user text
                        wordWrap: "break-word",   // optional extra safety
                        background:
                          m.role === "user"
                            ? "linear-gradient(135deg,#22c55e,#16a34a)"
                            : "rgba(15,23,42,0.97)",
                        color:
                          m.role === "user" ? "#f9fafb" : "#e5e7eb",
                        border:
                          m.role === "user"
                            ? "1px solid rgba(34,197,94,0.6)"
                            : "1px solid rgba(55,65,81,0.9)",
                        fontSize: 13,
                        boxShadow:
                          m.role === "user"
                            ? "0 10px 28px rgba(34,197,94,0.35)"
                            : "0 10px 30px rgba(0,0,0,0.7)",
                      }}
                    >
                      {m.role === "user" && (
                        <div>
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {m.text}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 8,
                              marginTop: 4,
                            }}
                          >
                            {editingMessageId === m.id ? (
                              <button
                                onClick={() => {
                                  setInput("");
                                  setEditingMessageId(null);
                                  setOriginalTextBeforeEdit("");
                                }}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#fecaca",
                                  fontSize: 11,
                                  cursor: "pointer",
                                }}
                              >
                                ✕ Cancel edit
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setOriginalTextBeforeEdit(m.text);
                                  setInput(m.text);
                                  setEditingMessageId(m.id);
                                  inputRef.current?.focus();
                                }}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#e5e7eb",
                                  fontSize: 11,
                                  cursor: "pointer",
                                }}
                              >
                                ✎ Edit &amp; resend
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {m.role === "assistant" && m.type === "chat" && (
                        <RichAnswer text={m.text} />
                      )}

                      {m.role === "assistant" && m.type === "sql" && (
                        <div>
                          {viewSQLArea && (
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#a5b4fc",
                                  marginBottom: 6,
                                  fontWeight: 600,
                                }}
                              >
                                SQL generated
                              </div>
                              <textarea
                                value={m.sql}
                                onChange={(e) =>
                                  handleSqlEdit(m.id, e.target.value)
                                }
                                style={{
                                  width: "100%",
                                  borderRadius: 10,
                                  border:
                                    "1px solid rgba(129,140,248,0.7)",
                                  background: "#020617",
                                  color: "#e5e7eb",
                                  padding: 8,
                                  fontSize: 12,
                                  fontFamily:
                                    "Menlo, ui-monospace, SFMono-Regular",
                                  resize: "vertical",
                                  minHeight: 70,
                                  marginBottom: 8,
                                }}
                              />
                              <button
                                onClick={() =>
                                  handleSqlReRun(m.id, m.sql)
                                }
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 999,
                                  border: "none",
                                  background:
                                    "linear-gradient(135deg,#22c55e,#16a34a)",
                                  color: "#f9fafb",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  marginBottom: 8,
                                }}
                              >
                                Run SQL
                              </button>
                            </div>
                          )}

                          {m.error && (
                            <div
                              style={{
                                background: "rgba(127,29,29,0.45)",
                                color: "#fecaca",
                                padding: 8,
                                borderRadius: 10,
                                border:
                                  "1px solid rgba(248,113,113,0.7)",
                                fontSize: 12,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              Error: {m.error}
                            </div>
                          )}

                          {m.rows && !m.error && (
                            <>
                              {m.rows.length === 0 ? (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 12,
                                    color: "#22c55e",
                                  }}
                                >
                                  Query ran successfully – no rows
                                  returned.
                                </div>
                              ) : (
                                <div
                                  style={{
                                    marginTop: 8,
                                    borderRadius: 10,
                                    border:
                                      "1px solid rgba(55,65,81,0.9)",
                                    overflow: "hidden",
                                    maxHeight: 260,
                                    overflowY: "auto",
                                    background: "#020617",
                                  }}
                                >
                                  <table
                                    style={{
                                      borderCollapse: "collapse",
                                      width: "100%",
                                      fontSize: 11,
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{
                                          background:
                                            "linear-gradient(135deg,#1d4ed8,#0ea5e9)",
                                          color: "#e5e7eb",
                                        }}
                                      >
                                        {m.columns.map((c, i) => (
                                          <th
                                            key={i}
                                            style={{
                                              padding: 6,
                                              textAlign: "left",
                                              borderRight:
                                                "1px solid rgba(30,64,175,0.7)",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {c}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.rows
                                        .slice(0, 50)
                                        .map((r, ri) => (
                                          <tr
                                            key={ri}
                                            style={{
                                              background: "#020617",
                                              borderTop:
                                                "1px solid rgba(31,41,55,0.9)",
                                            }}
                                          >
                                            {r
                                              .split("|")
                                              .map((cell, ci) => (
                                                <td
                                                  key={ci}
                                                  style={{
                                                    padding: 6,
                                                    borderRight:
                                                      "1px solid rgba(31,41,55,0.8)",
                                                    color: "#e5e7eb",
                                                    wordBreak:
                                                      "break-word",
                                                  }}
                                                >
                                                  {cell === "NULL" ? (
                                                    <span
                                                      style={{
                                                        color: "#6b7280",
                                                      }}
                                                    >
                                                      NULL
                                                    </span>
                                                  ) : (
                                                    cell || "-"
                                                  )}
                                                </td>
                                              ))}
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              <div
                                style={{
                                  marginTop: 6,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                {m.rows && m.rows.length > 0 && (
                                  <button
                                    onClick={() =>
                                      downloadCsv(
                                        m.columns,
                                        m.rows,
                                        "query-results.csv"
                                      )
                                    }
                                    style={{
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      border:
                                        "1px solid rgba(148,163,184,0.7)",
                                      background: "transparent",
                                      color: "#e5e7eb",
                                      fontSize: 11,
                                      fontWeight: 500,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Download CSV
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* {m.role === "assistant" &&
                        m.type === "report" &&
                        m.report && (
                          <div>
                            <h4
                              style={{
                                fontSize: 15,
                                fontWeight: 600,
                                marginBottom: 4,
                              }}
                            >
                              {m.report.title}
                            </h4>
                            <p
                              style={{
                                fontSize: 12,
                                color: "#d1d5db",
                                marginBottom: 8,
                              }}
                            >
                              {m.report.summary}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                marginBottom: 8,
                              }}
                            >
                              {m.report.kpis?.map((k, i) => (
                                <div
                                  key={i}
                                  style={{
                                    padding: "6px 10px",
                                    borderRadius: 10,
                                    border:
                                      "1px solid rgba(148,163,184,0.5)",
                                    background: "#020617",
                                    fontSize: 11,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: "#9ca3af",
                                    }}
                                  >
                                    {k.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {k.value} {k.unit}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {(() => {
                              const { labelColumn, valueColumn } = m.report || {};
                              const { labels, values } = buildChartFromResult(
                                m.columns || [],
                                m.rows || [],
                                labelColumn,
                                valueColumn
                              );
                              if (!labels.length) return null;

                              const chartData = {
                                type: "bar",
                                labels,
                                values,
                              };

                              return (
                                <>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      marginBottom: 8,
                                    }}
                                  >
                                    <button
                                      onClick={() => setChartType("bar")}
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border:
                                          chartType === "bar"
                                            ? "1px solid #22c55e"
                                            : "1px solid #4b5563",
                                        background:
                                          chartType === "bar" ? "#022c22" : "transparent",
                                        color: "#e5e7eb",
                                        fontSize: 12,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Bar chart
                                    </button>
                                    <button
                                      onClick={() => setChartType("pie")}
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border:
                                          chartType === "pie"
                                            ? "1px solid #22c55e"
                                            : "1px solid #4b5563",
                                        background:
                                          chartType === "pie" ? "#022c22" : "transparent",
                                        color: "#e5e7eb",
                                        fontSize: 12,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Pie chart
                                    </button>
                                  </div>

                                  {chartType === "bar" ? (
                                    <div
                                      style={{
                                        maxWidth: "100%",        // do not exceed message bubble
                                        maxHeight: 320,          // fixed height within message
                                        overflowX: "auto",       // horizontal scroll for many bars
                                        overflowY: "auto",       // vertical scroll if needed
                                        paddingBottom: 4,
                                      }}
                                    >
                                      <ReportBarChart chart={chartData} />
                                    </div>
                                  ) : (

                                    <div
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: 320,
                                        overflow: "auto",
                                        paddingBottom: 4,
                                      }}
                                    >
                                      <ReportPieChart chart={chartData} />
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )} */}

                      {m.role === "assistant" &&
                        m.type === "report" &&
                        m.report && (
                          <div>
                            <h4
                              style={{
                                fontSize: 15,
                                fontWeight: 600,
                                marginBottom: 4,
                              }}
                            >
                              {m.report.title}
                            </h4>
                            <p
                              style={{
                                fontSize: 12,
                                color: "#d1d5db",
                                marginBottom: 8,
                              }}
                            >
                              {m.report.summary}
                            </p>

                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                marginBottom: 8,
                              }}
                            >
                              {m.report.kpis?.map((k, i) => (
                                <div
                                  key={i}
                                  style={{
                                    padding: "6px 10px",
                                    borderRadius: 10,
                                    border: "1px solid rgba(148,163,184,0.5)",
                                    background: "#020617",
                                    fontSize: 11,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: "#9ca3af",
                                    }}
                                  >
                                    {k.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {k.value} {k.unit}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {(() => {
                              const { labelColumn, valueColumn } = m.report || {};
                              const { labels, values } = buildChartFromResult(
                                m.columns || [],
                                m.rows || [],
                                labelColumn,
                                valueColumn
                              );
                              if (!labels.length) return null;

                              const chartData = {
                                type: "bar",
                                labels,
                                values,
                              };

                              // chart type for THIS message
                              const typeForThis = chartTypes[m.id] || "bar";

                              return (
                                <>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      marginBottom: 8,
                                    }}
                                  >
                                    <button
                                      onClick={() =>
                                        setChartTypes((prev) => ({ ...prev, [m.id]: "bar" }))
                                      }
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border:
                                          typeForThis === "bar"
                                            ? "1px solid #22c55e"
                                            : "1px solid #4b5563",
                                        background:
                                          typeForThis === "bar" ? "#022c22" : "transparent",
                                        color: "#e5e7eb",
                                        fontSize: 12,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Bar chart
                                    </button>
                                    <button
                                      onClick={() =>
                                        setChartTypes((prev) => ({ ...prev, [m.id]: "pie" }))
                                      }
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border:
                                          typeForThis === "pie"
                                            ? "1px solid #22c55e"
                                            : "1px solid #4b5563",
                                        background:
                                          typeForThis === "pie" ? "#022c22" : "transparent",
                                        color: "#e5e7eb",
                                        fontSize: 12,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Pie chart
                                    </button>
                                  </div>

                                  {typeForThis === "bar" ? (
                                    <div
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: 320,
                                        overflowX: "auto",
                                        overflowY: "auto",
                                        paddingBottom: 4,
                                      }}
                                    >
                                      <ReportBarChart chart={chartData} />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: 320,
                                        overflow: "auto",
                                        paddingBottom: 4,
                                      }}
                                    >
                                      <ReportPieChart chart={chartData} />
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}


                      {/* {m.role === "assistant" && m.type === "report" && m.report && (
                        <div>
                          <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                            {m.report.title}
                          </h4>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#d1d5db",
                              marginBottom: 8,
                            }}
                          >
                            {m.report.summary}
                          </p>

                          <DashboardSection
                            report={m.report}
                            columns={m.columns || []}
                            rows={m.rows || []}
                          />
                        </div>
                      )} */}


                    </div>
                  </div>
                ))}



                {loading && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                      fontSize: 12,
                      color: "#9ca3af",
                    }}
                  >
                    <div style={{ display: "flex", gap: 4 }}>
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "999px",
                            background: "#22c55e",
                            opacity: 0.4,
                            animation: "papy-bounce 1s infinite",
                            animationDelay: `${i * 0.15}s`,
                          }}
                        />
                      ))}
                    </div>
                    <span>Papy AI is thinking…</span>
                  </div>
                )}


                <div ref={bottomRef} />
              </div>
            </div>

            {/* Input bar */}
            <div
              style={{
                borderTop: "1px solid rgba(31,41,55,0.9)",
                padding: "10px 18px 14px",
                background:
                  "linear-gradient(180deg,rgba(15,23,42,0.98),rgba(15,23,42,1))",
              }}
            >
              <div
                style={{
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-end",
                  }}
                >
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything about your data…"
                    style={{
                      flex: 1,
                      maxHeight: 120,
                      minHeight: 44,
                      resize: "none",
                      borderRadius: 999,
                      border: "1px solid rgba(55,65,81,0.9)",
                      background: "rgba(15,23,42,0.95)",
                      color: "#e5e7eb",
                      padding: "10px 16px",
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={
                      loading || !input.trim() || !activeThreadId
                    }
                    style={{
                      width: 82,
                      padding: "10px 0",
                      borderRadius: 999,
                      border: "none",
                      background:
                        loading || !input.trim() || !activeThreadId
                          ? "#374151"
                          : "linear-gradient(135deg,#22c55e,#16a34a)",
                      color: "#f9fafb",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor:
                        loading || !input.trim() || !activeThreadId
                          ? "not-allowed"
                          : "pointer",
                      textAlign: "center",
                    }}
                  >
                    {loading ? "…" : "Send"}
                  </button>
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 10,
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  Enter to send · Shift+Enter for new line.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


